import React, { SyntheticEvent, useState } from 'react';
import { Collapse, Grid, List, ListItemText } from '@mui/material';
import { EditOutlined, ExpandMore, ChevronRightOutlined } from '@mui/icons-material';
import { Typography, TypographyFontWeightProps, TypographyVariantProps } from '../../atoms/Typography';
import { LocationMapAccordianProps } from './LocationMapAccordian.props';
import { LocationGrid, ContactTypography, StyledListItem, ZoneTypography, MapBox } from './LocationMapAccordian.styles';
import { Accordian } from '../../atoms/Accordian';
import { OtherServiceList } from '../OtherServiceList';
import { Button, ButtonVariantProps, ButtonSizeProps, ButtonColorProps } from '../../atoms/Button';
import { Link } from '../../atoms/Link';
import { ExternalLink } from '../../atoms/ExternalLink';
import { AddressContactType, NodeAttributeType, NodeTypeCode } from '../../../configs/enums';
import { getServiceLocationAccordionAddress, getServiceOrPartLink } from '../../../utils/services';
import { ServiceLocationHierarchy } from '../../../@types/service.type';
import { LinkColorProps, FontStyleProps } from '../../../@types/common.type';
import { GOOGLE_MAP_URL } from '../../../configs/url-constants';

export const LocationMapAccordian: React.FC<LocationMapAccordianProps> = ({
  otherServiceData,
  nodeAttributes,
  serviceAttributes,
  bulkEditServiceDropDownsDataset,
  bulkUpdateToastMessage,
  parentServiceId,
  serviceAddress,
  handleBulkUpdateSave,
  setIsEdit,
  expandOnMobile,
  otherServicesLoading,
  locationHierarchy,
  data
}) => {
  const [expanded, setExpanded] = useState(expandOnMobile);
  const [isShown, setIsShown] = useState(false);
  let embedUrl: string = '';

  const mainAddress = nodeAttributes?.find((item) => item.propertyTypeCode === NodeAttributeType.Address && item.value.addressType === AddressContactType.Main)?.value;
  const logisticsAddress = nodeAttributes?.find((item) => item.propertyTypeCode === NodeAttributeType.Address && item.value.addressType === AddressContactType.Logistics)?.value;
  const mainContact = nodeAttributes?.find((item) => item.propertyTypeCode === NodeAttributeType.Contact && item.value.contactType === AddressContactType.Main)?.value;
  const logisticsContact = nodeAttributes?.find((item) => item.propertyTypeCode === NodeAttributeType.Contact && item.value.contactType === AddressContactType.Logistics)?.value;
  const tradingHours = nodeAttributes?.filter((item) => item.propertyTypeCode === NodeAttributeType.TradingHours);
  const serviceContact = serviceAttributes?.find((item) => item.code === NodeAttributeType.Contact && item.value.contactType === AddressContactType.Main)?.value;

  // for future use
  // const src = `${GOOGLE_MAP_API_URL}/api/staticmap?center=${mainAddress?.googleAddress || ''}&zoom=11&size=180x35&markers=size:mid%7Ccolor:red%7C${mainAddress?.googleAddress || ''}&key=${GOOGLE_MAPS_API_KEY}`;

  const getFullAddress = (addr?: any) => {
    if (!addr) return '';
    return addr.googleAddress
      ? addr.googleAddress
      : `${addr.line1}, ${addr.suburb}, ${addr.stateName} ${addr.postCode}, ${addr.countryName || addr.country}`;
  };

  if (mainAddress || logisticsAddress || serviceAddress) {
    const address =
      mainAddress?.countryName || mainAddress?.googleAddress
        ? mainAddress
        : logisticsAddress?.countryName || logisticsAddress?.googleAddress
          ? logisticsAddress
          : serviceAddress;

    const googleAddress = getFullAddress(address);

    if (googleAddress) {
      const mapPath = `${GOOGLE_MAP_URL}?q=${encodeURIComponent(googleAddress)}`;
      embedUrl = `${mapPath}&output=embed`;
    }
  }

  const TreeView: React.FC<{ node: ServiceLocationHierarchy }> = ({ node }) => {
    const [open, setOpen] = useState(node.id === data?.mainLocationNodeId || !node.parentTree.includes(data?.mainLocationNodeId?.toString() || ''));

    return (
      <>
        <StyledListItem onClick={() => setOpen(!open)}>
          {node.children.length > 0 ? (open ? <ExpandMore /> : <ChevronRightOutlined />) : <>&nbsp;&nbsp;</>}
          <ListItemText primary={
            <Grid>
              {node.nodeTypeCode !== NodeTypeCode.Brand ?
                <Typography
                  fontStyle={!node.isActive ? FontStyleProps.Italic : FontStyleProps.Regular}
                  fontWeight={node.id === data?.mainLocationNodeId ? TypographyFontWeightProps.Bold : TypographyFontWeightProps.Regular} >
                  {data?.isSiblingInServiceClientHierarchy ?
                    <Link href={`structure/${node.id}`} target="_blank" color={!node.isActive ? LinkColorProps.TextSecondary : LinkColorProps.Primary}>
                      {node.name}
                    </Link>
                    :
                    node.name
                  }
                  <span>&nbsp;</span>
                  ({node.serviceCount !== 0 ?
                    data?.isSiblingInServiceClientHierarchy ?
                      <Link href={getServiceOrPartLink('service', node.id)} target="_blank" color={!node.isActive ? LinkColorProps.TextSecondary : LinkColorProps.Primary}>
                        {node.serviceCount} services
                      </Link>
                      :
                      `${node.serviceCount} services`
                    : `${node.serviceCount} services`
                  },
                  <span>&nbsp;</span>
                  {node.partCount !== 0 ?
                    data?.isSiblingInServiceClientHierarchy ?
                      <Link href={getServiceOrPartLink('part', node.name)} target="_blank" color={!node.isActive ? LinkColorProps.TextSecondary : LinkColorProps.Primary}>
                        {node.partCount} parts
                      </Link>
                      :
                      `${node.partCount} parts`
                    : `${node.partCount} parts`
                  })
                  {!node.isActive && ' (inactive)'}
                </Typography>
                :
                <Typography fontStyle={!node.isActive ? FontStyleProps.Italic : FontStyleProps.Regular}>{node.name}{!node.isActive && ' (inactive)'}</Typography>
              }
            </Grid>
          } />
        </StyledListItem>
        <Collapse in={open} timeout="auto" unselectable="on">
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {node.children.map((child) => (
              <TreeView key={child.id} node={child} />
            ))}
          </List>
        </Collapse>
      </>
    );
  };

  return (
    <>
      <Accordian
        expanded={expanded}
        onChange={(event: SyntheticEvent, newExpanded: boolean) => setExpanded(newExpanded)}
        title={expanded ?
          <Grid container alignItems="center" size={{ sm: 'grow' }}>
            <Grid mr={7}>
              <Typography>Location</Typography>
            </Grid>
          </Grid>
          :
          <>
            <Grid container justifyContent="space-between" alignItems="center" width="100%">
              <Grid
                alignItems="center"
                container
                size={{
                  sm: 'grow'
                }}>
                <Grid mr={3}>
                  <Typography>Location</Typography>
                </Grid>
                {(serviceAddress || mainAddress) ?
                  <Grid mr={4}>
                    <Typography variant={TypographyVariantProps.Body1}>
                      {getServiceLocationAccordionAddress(serviceAddress || mainAddress)}
                    </Typography>
                  </Grid>
                  : ''
                }
              </Grid>
              {(serviceContact || mainContact) ?
                <Grid mr={3}>
                  <ExternalLink href={`tel:${serviceContact?.phone1 || mainContact?.phone1}`}>
                    <Typography>{serviceContact?.phone1 || mainContact?.phone1}</Typography>
                  </ExternalLink>
                </Grid>
                : ''
              }
            </Grid>
          </>
        }>
        <LocationGrid container spacing={6}>
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Grid mb={1}>
              <Typography fontWeight={TypographyFontWeightProps.Bold}>Main Address</Typography>
              {mainAddress && (
                <>
                  <Typography>{mainAddress?.placeName}</Typography>
                  <Typography>{mainAddress?.line1}</Typography>
                  <Typography>{`${mainAddress?.suburb} ${mainAddress?.stateName} ${mainAddress?.postCode}`}</Typography>
                  {data?.mainLocationZone && <>
                    <ZoneTypography fontWeight={TypographyFontWeightProps.Bold}>{'Zone'}</ZoneTypography>
                    <Typography>{data.mainLocationZone}</Typography>
                  </>}
                </>
              )}
              {mainContact && (
                <>
                  {mainContact?.phone1 && <ContactTypography>Phone1: {`${mainContact?.phone1}`}</ContactTypography>}
                  {mainContact?.phone2 && <Typography>Phone2: {`${mainContact?.phone2}`}</Typography>}
                </>
              )}
            </Grid>
            <Grid mb={1} display={logisticsAddress ? '' : 'none'}>
              <Typography fontWeight={TypographyFontWeightProps.Bold} variant={TypographyVariantProps.Body2}>Logistics Address</Typography>
              {logisticsAddress && (
                <>
                  <Typography variant={TypographyVariantProps.Body2}>{logisticsAddress?.placeName}</Typography>
                  <Typography variant={TypographyVariantProps.Body2}>{logisticsAddress?.line1}</Typography>
                  <Typography variant={TypographyVariantProps.Body2}>{`${logisticsAddress?.suburb} ${logisticsAddress?.stateName} ${logisticsAddress?.postCode}`}</Typography>
                </>
              )}
              {logisticsContact && (
                <>
                  {logisticsContact?.phone1 && <ContactTypography>Phone1: {`${logisticsContact?.phone1}`}</ContactTypography>}
                  {logisticsContact?.phone2 && <Typography>Phone2: {`${logisticsContact?.phone2}`}</Typography>}
                </>
              )}
            </Grid>
            <Grid mb={4} display={tradingHours ? '' : 'none'}>
              <Typography fontWeight={TypographyFontWeightProps.Bold}>Trading Hours</Typography>
              {tradingHours?.map((item) => <Typography key={item.id}>{item.value.type}: {item.value.start} - {item.value.end}</Typography>)}
            </Grid>
            {serviceAddress ?
              <Grid
                mb={1}
                onMouseEnter={() => setIsShown(true)}
                onMouseLeave={() => setIsShown(false)}>
                <Grid container direction="row">
                  <Typography fontWeight={TypographyFontWeightProps.Bold}>Service Address</Typography>
                  {isShown &&
                    <Grid position="absolute" ml={18}>
                      <Button
                        variant={ButtonVariantProps.Icon}
                        size={ButtonSizeProps.Small}
                        onClick={() => setIsEdit && setIsEdit(true)}
                      >
                        <EditOutlined fontSize="small" color={ButtonColorProps.Warning} />
                      </Button>
                    </Grid>
                  }
                </Grid>
                {serviceAddress && (
                  <>
                    <Typography>{serviceAddress?.placeName}</Typography>
                    <Typography>{serviceAddress?.line1}</Typography>
                    <Typography>{`${serviceAddress?.suburb} ${serviceAddress?.stateName} ${serviceAddress?.postCode}`}</Typography>
                    {data?.zone && <>
                      <ZoneTypography fontWeight={TypographyFontWeightProps.Bold}>{'Zone'}</ZoneTypography>
                      <Typography>{data.zone}</Typography>
                    </>}
                  </>
                )}
                {serviceContact &&
                  <>
                    {serviceContact?.phone1 && <ContactTypography>Phone1: {`${serviceContact?.phone1}`}</ContactTypography>}
                    {serviceContact?.phone2 && <Typography>Phone2: {`${serviceContact?.phone2}`}</Typography>}
                  </>
                }
              </Grid>
              :
              <Grid
                mb={1}
                onMouseEnter={() => setIsShown(true)}
                onMouseLeave={() => setIsShown(false)}>
                <Grid container direction="row">
                  <Typography fontWeight={TypographyFontWeightProps.Bold}>[Service] Address</Typography>
                  {isShown &&
                    <Grid position="absolute" ml={18}>
                      <Button
                        variant={ButtonVariantProps.Icon}
                        size={ButtonSizeProps.Small}
                        onClick={() => setIsEdit && setIsEdit(true)}
                      >
                        <EditOutlined fontSize="small" color={ButtonColorProps.Warning} />
                      </Button>
                    </Grid>
                  }
                </Grid>
                {mainAddress && (
                  <>
                    <Typography>{mainAddress?.placeName}</Typography>
                    <Typography>{mainAddress?.line1}</Typography>
                    <Typography>{`${mainAddress?.suburb} ${mainAddress?.stateName} ${mainAddress?.postCode}`}</Typography>
                    {data?.mainLocationZone && <>
                      <ZoneTypography fontWeight={TypographyFontWeightProps.Bold}>{'Zone'}</ZoneTypography>
                      <Typography>{data.mainLocationZone}</Typography>
                    </>}
                  </>
                )}
                {mainContact && (
                  <>
                    {mainContact?.phone1 && <ContactTypography>Phone1: {`${mainContact?.phone1}`}</ContactTypography>}
                    {mainContact?.phone2 && <Typography>Phone2: {`${mainContact?.phone2}`}</Typography>}
                  </>
                )}
              </Grid>
            }
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            {locationHierarchy &&
              <>
                <Typography fontWeight={TypographyFontWeightProps.Bold}>Structure Hierarchy</Typography>
                <List>
                  <TreeView node={locationHierarchy} />
                </List>
              </>
            }
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            {embedUrl &&
              <>
                <Typography fontWeight={TypographyFontWeightProps.Bold}>Map</Typography>
                <MapBox onClick={() => window.open(embedUrl, '_blank')}>
                  <iframe
                    src={embedUrl}
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Location Map"
                  ></iframe>
                </MapBox>
              </>
            }
          </Grid>
        </LocationGrid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Grid mt={2}>
            <Typography fontWeight={TypographyFontWeightProps.Bold}>Other Services</Typography>
            <OtherServiceList
              data={otherServiceData || []}
              bulkEditServiceDropDownsDataset={bulkEditServiceDropDownsDataset}
              bulkUpdateToastMessage={bulkUpdateToastMessage}
              handleBulkUpdateSave={handleBulkUpdateSave}
              parentServiceId={parentServiceId}
              otherServicesLoading={otherServicesLoading}
            />
          </Grid>
        </Grid>
      </Accordian >
    </>
  );
};
